import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import UploadFile from "components/Input/UploadFile";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";

const METHODS = ["BANK_TRANSFER", "CASH", "CHEQUE", "OTHER"];

const todayIso = () => new Date().toISOString().slice(0, 10);

/**
 * Records a payout for the selected approved rows. The dialog owns the form; the parent owns
 * the mutation and refreshes the ledger when it succeeds.
 */
const PayoutDialog = ({ open, rowCount, selectedTotal, onClose, onSubmit, submitting }) => {
  const [reference, setReference] = useState("");
  const [method, setMethod] = useState("BANK_TRANSFER");
  const [paidDate, setPaidDate] = useState(todayIso());
  const [note, setNote] = useState("");
  const [upload, setUpload] = useState({ receipts: [] });

  useEffect(() => {
    if (open) setUpload({ receipts: [] });
  }, [open]);

  const valid = rowCount > 0 && paidDate;

  const handleSubmit = () => {
    if (!valid) return;
    onSubmit({
      reference: reference.trim(),
      method,
      paidDate,
      note: note.trim(),
      receiptDocuments: (upload.receipts || []).filter(Boolean),
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Record payout</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {rowCount} approved row{rowCount === 1 ? "" : "s"} totalling{" "}
          <strong>
            ${Number(selectedTotal || 0).toLocaleString("en-AU", { minimumFractionDigits: 2 })}
          </strong>
          . They move to paid under this reference.
        </Typography>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <TextField
            label="Reference"
            placeholder="e.g. TX-9001"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
          />
          <TextField
            select
            label="Method"
            value={method}
            onChange={(e) => setMethod(e.target.value)}
          >
            {METHODS.map((m) => (
              <MenuItem key={m} value={m}>
                {m.replace("_", " ")}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Paid date"
            type="date"
            value={paidDate}
            onChange={(e) => setPaidDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </div>
        <div className="mt-4">
          <UploadFile
            data={upload}
            setData={setUpload}
            imageKey="receipts"
            label="Transfer receipt / proof (optional)"
            type="multiple"
            newDesign
          />
        </div>
        <TextField
          fullWidth
          multiline
          rows={2}
          label="Note (optional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          sx={{ mt: 2 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!valid || submitting} onClick={handleSubmit}>
          {submitting ? "Recording…" : "Record payout"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const payoutToast = (err) =>
  toast.error(
    err?.response?.data?.errorMessage || err?.response?.data?.message || "Could not record payout"
  );

export const StatusChipMini = ({ status, label }) => {
  const colors = {
    ACCRUED: "info",
    APPROVED: "warning",
    PAID: "success",
    CANCELLED: "default",
    PART_APPROVED: "info",
    PART_PAID: "warning",
  };
  return <Chip label={label || status} color={colors[status] || "default"} size="small" />;
};

export default PayoutDialog;
